import {
  Text,
  GreyGrey70,
  GreyGrey30,
  Primary13Regular,
  Primary13SemiBold,
  Flex
} from '@sequencehq/core-components'
import { Skeleton } from '@sequencehq/core-components'

type RowAlignment = 'center' | 'flex-start'

const SummaryInfoRow = ({
  title,
  value,
  borderBottom = true,
  isLoading = false,
  align = 'center'
}: {
  title: string
  value: React.ReactNode | string
  borderBottom?: boolean
  isLoading?: boolean
  align?: RowAlignment
}) => {
  if (isLoading) {
    return (
      <Flex height="32px" justifyContent="space-between" alignItems="center">
        <Skeleton height="16px" width="30%" />
        <Skeleton height="16px" width="30%" />
      </Flex>
    )
  }

  // py on flex-start rows keeps the single-line min-height visually centred
  // while allowing wrapping values to align their first line with the label.
  const isTopAligned = align === 'flex-start'

  return (
    <Flex
      minHeight="32px"
      justifyContent="space-between"
      alignItems={align}
      gap="16px"
      py={isTopAligned ? '7px' : undefined}
      borderBottom={borderBottom ? `1px solid ${GreyGrey30}` : 'none'}
    >
      <Text {...Primary13Regular} color={GreyGrey70}>
        {title}
      </Text>
      {typeof value === 'string' ? (
        <Text {...Primary13Regular} maxWidth="60%" py="2px">
          {value}
        </Text>
      ) : (
        value
      )}
    </Flex>
  )
}

type SummaryProps = {
  rows: {
    title: string
    value: React.ReactNode | string
    align?: RowAlignment
  }[]
  isLoading?: boolean
}

export const Summary = ({ rows, isLoading }: SummaryProps) => {
  return (
    <Flex flexDirection="column">
      <Flex pt="4px" pb="8px">
        <Text {...Primary13SemiBold}>Summary</Text>
      </Flex>
      {rows.map((row, index) => (
        <SummaryInfoRow
          key={row.title}
          title={row.title}
          value={row.value}
          align={row.align}
          borderBottom={index !== rows.length - 1}
          isLoading={isLoading}
        />
      ))}
    </Flex>
  )
}
